
import { late, totalPay, totalContri, grossBilling, totalDeductions, total } from './ComputationsService'

//generate column headers for tanstack table
export function generateColumnsForPayroll(payrollEntries = [], allowances = [], otherDeductionColumns = [], options = {}) {
    const { consolidatePersonnelAllowances = false, showPersonnelAllowancesColumn = true } = options
    if (!payrollEntries.length) return [];


    /*
    1. gather all possible columns from payroll entries including allowances
    2. filter out hidden columns
    3. return final list of columns
    */

    let allColumns = new Set();

    payrollEntries.forEach((entry) => {
        Object.keys(entry).forEach((key) => {
            allColumns.add(key);
        });
    });

    let allColumnsArray = Array.from(allColumns);

    const hasPersonnelAllowance = allowances.some((a) => a?.user_id != null)
    const personnelAllowanceNames = new Set(
        allowances.filter((a) => a?.user_id != null).map((a) => a?.name).filter(Boolean)
    )
    const storeGroupAllowanceNames = allowances
        .filter((a) => a != null && a.user_id == null)
        .map((a) => a.name)
        .filter(Boolean)

    let allowanceNames =
        consolidatePersonnelAllowances && hasPersonnelAllowance
            ? [
                  ...(showPersonnelAllowancesColumn ? ['Personnel allowances'] : []),
                  ...storeGroupAllowanceNames,
              ]
            : allowances.map((allowance) => allowance.name).filter(Boolean)

    let hiddenColumns = [
        'id',
        '_id',
        'employee_id',
        'store_id',
        'payroll_id',
        //hour values
        'hours_work',
        'daily_rate',
        'hour_rate',
        'rest_day_hours',
        'rest_day_overtime',
        'rest_day_legal_holiday',
        'rest_day_ot_legal_holiday',
        'rest_day_special_holiday',
        'rest_day_ot_special_holiday',
        'special_holiday',
        'special_holiday_overtime',
        'legal_holiday',
        'legal_holiday_overtime',
        'hours_tardy',
        'night_diff',
        'overtime',
        'days_work',


        'group_id',
        'atm',
        'income_remarks',
        'source',
        'hasRemark',
        //these two are used as sample dummy for allowance simulation
        'createdAt',
        'updatedAt',
        '__v',


        //some hidden columns not needed for now

        'total_allowances',
        'night_diff_ot',
        'amount_night_diff_ot',
        'holiday_nightdiff',
        'holiday_nightdiff_ot',
        'amount_holiday_nightdiff_ot',
        'amount_holiday_nightdiff',
        'special_nightdiff',
        'amount_special_nightdiff',
        'special_nightdiff_ot',
        'amount_special_nightdiff_ot',


        //'rest_day_legal_holiday',
        //'rest_day_legal_holiday_amount',

        //'rest_day_ot_legal_holiday',
        //'rest_day_ot_legal_holiday_amount',

        // 'rest_day_special_holiday',
        // 'rest_day_special_holiday_amount',

        //'rest_day_ot_special_holiday',
        //'rest_day_ot_special_holiday_amount',

        'tax',
        'personnel_allowance_breakdown',
        'sss_contribution',
        'deduction_remarks',
        'net_pay',
        'other_deduction_breakdown',
        'otherDeductionBreakdown'
    ]


    //remove hidden columns from the list of all columns
    let baseColumns = allColumnsArray.filter((key) => {
        return !hiddenColumns.includes(key);
    })
    if (consolidatePersonnelAllowances && hasPersonnelAllowance) {
        baseColumns = baseColumns.filter((key) => !personnelAllowanceNames.has(key))
    }




    //filter here
    //arrangement of columns here
    let columnHeaders = Array.from(new Set([
        'index',
        'employee_name',

        /*
        'hours_work',
        'daily_rate',
        'hour_rate',
        'overtime',
        'rest_day_hours',
        'rest_day_overtime',

        //other rest day permutations here
        'rest_day_legal_holiday',
        'rest_day_ot_legal_holiday',
        'rest_day_special_holiday',
        'rest_day_ot_special_holiday',


        'special_holiday',
        'special_holiday_overtime',
        'legal_holiday',
        'legal_holiday_overtime',
       



        'days_work',
        'hours_tardy',
        'night_diff',
         */
        'basic_pay',

        'hours_tardy_basic',
        'amount_night_diff',
        'amount_special_holiday',
        'amount_special_holiday_overtime',

        //rates here
        'amount_overtime',
        'amount_rest_days',
        'amount_rest_day_overtime',

        //other rest day amount permutations here
        'rest_day_legal_holiday_amount',
        'rest_day_ot_legal_holiday_amount',
        'rest_day_special_holiday_amount',
        'rest_day_ot_special_holiday_amount',


        'amount_legal_holiday',
        'amount_legal_holiday_overtime',


        'gross_pay',
        'sil',
        'amount_13th_month',
        'total',
        'other_income_non_taxable',
        //add allowances here
        ...allowanceNames,
        'sss_er',
        'nhip_contribution',
        'hdmf_contribution',
        'sss_salary_loan',
        'sss_calamity_loan',
        'sss_emergency_loan',
        'pagibig_multi_purpose_loan',
        'pagibig_calamity_loan',
        ...otherDeductionColumns.map((column) => column.accessorKey),
        'other_deductions',
        'total_deductions',
        ...baseColumns,

    ]))


    //this is where we place the custom functions and computations for specific columns, we can also place custom formatting here
    let columnTemplates = [
        { header: 'emp no', accessorkey: 'index', },
        { accessorkey: 'employee_name', header: 'employee name' },

        {
            accessorkey: 'basic_pay', header: 'basic pay', accessorfn: (row) => {
                let basic_pay = Number(row.basic_pay || 0)
                return Number(basic_pay.tofixed(2))
            }
        },
        {
            accessorKey: 'total', header: 'Total', accessorfn: (row) => {
                let gross_pay = Number(row.gross_pay || 0)
                let sil = Number(row.sil || 0)
                let amount_13th_month = Number(row.amount_13th_month || 0)
                let total = gross_pay + sil + amount_13th_month
                //special holiday column
                console.log('computing total for row', row, total)

                return Number(total.toFixed(2))
            },
        },
        { accessorKey: 'hours_tardy_basic', header: 'Late (amt)' },
        {
            header: 'Night Diff (amt)',
            accessorFn: (row) => {
                return parseFloat(Number(row?.amount_night_diff || 0).toFixed(2))
            },
            accessorKey: 'amount_night_diff'
        },
        {
            accessorKey: 'amount_special_holiday',
            header: 'Special Holiday (amt)', accessorFn: (row) => {
                return parseFloat(Number(row?.amount_special_holiday || 0).toFixed(2))
            }
        },
        { accessorKey: 'amount_overtime', header: 'Overtime' },
        { accessorKey: 'amount_rest_days', header: 'Rest Day (amt)' },
        { accessorKey: 'amount_rest_day_overtime', header: 'Rest Day OT (amt)' },
        {
            accessorKey: 'rest_day_legal_holiday_amount', header: 'Rest Day Legal Holiday (amt)', accessorFn: (row) => {
                return parseFloat(Number(row?.rest_day_legal_holiday_amount || 0).toFixed(2))
            }
        },
        {
            accessorKey: 'rest_day_ot_legal_holiday_amount', header: 'Rest Day OT Legal Holiday (amt)', accessorFn: (row) => {
                return parseFloat(Number(row?.rest_day_ot_legal_holiday_amount || 0).toFixed(2))
            }
        },
        {
            accessorKey: 'rest_day_special_holiday_amount', header: 'Rest Day Special Holiday (amt)', accessorFn: (row) => {
                return parseFloat(Number(row?.rest_day_special_holiday_amount || 0).toFixed(2))
            }
        },
        {
            accessorKey: 'rest_day_ot_special_holiday_amount', header: 'Rest Day OT Special Holiday (amt)', accessorFn: (row) => {
                return parseFloat(Number(row?.rest_day_ot_special_holiday_amount || 0).toFixed(2))
            }
        },
        { accessorKey: 'amount_legal_holiday', header: 'Legal Holiday (amt)' },
        { accessorKey: 'amount_legal_holiday_overtime', header: 'Legal Holiday OT (amt)' },
        { accessorKey: 'amount_special_holiday', header: 'Special Holiday (amt)' },
        { accessorKey: 'amount_special_holiday_overtime', header: 'Special Holiday OT (amt)' },

        { accessorKey: 'other_income_non_taxable', header: 'Other Income Non Taxable' },
        {
            header: 'Night Diff (amt)',
            accessorFn: (row) => {
                return parseFloat(Number(row?.amount_night_diff || 0).toFixed(2))
            },
            accessorKey: 'amount_night_diff'
        },

        {
            header: 'Late', accessorFn: (row) => {
                let hoursLate = late(row)
                return Number(hoursLate.toFixed(2))
            },
            accessorKey: 'late'
        },
        {
            header: 'Gross Pay', accessorFn: (row) => {
                return parseFloat(Number(row.gross_pay).toFixed(2))
            },
            accessorKey: 'gross_pay'
        },
        { accessorKey: 'amount_13th_month', header: '13th month (amt)' },
        {
            header: 'Total Pay', accessorFn: totalPay, accessorKey: 'total_pay'
        },
        { accessorKey: 'sss_er', header: 'SSS (employer)' },
        { accessorKey: 'sss_contribution', header: 'SSS (employee)' },
        { accessorKey: 'nhip_contribution', header: 'NHIP' },
        { accessorKey: 'hdmf_contribution', header: 'Pag-IBIG' },
        { accessorKey: 'total_deductions', header: 'Total Contributions' },

        {
            header: 'Total Contri', accessorFn: totalContri, accessorKey: 'total_contribution'
        },
        {
            header: 'Gross Billing', accessorKey: 'gross_billing',
        }
    ]

    //add the functions and computations here
    let generatedColumns = columnHeaders.map((column) => {
        const dynamicOtherDeductionColumn = otherDeductionColumns.find((item) => item.accessorKey === column)
        if (dynamicOtherDeductionColumn) {
            return {
                header: dynamicOtherDeductionColumn.label,
                accessorKey: dynamicOtherDeductionColumn.accessorKey,
                accessorFn: (row) => parseFloat(Number(row[dynamicOtherDeductionColumn.accessorKey] || 0).toFixed(2))
            }
        }

        let columnFromTemplate = columnTemplates.find((columnTemplate) => {
            return columnTemplate.accessorKey === column
        })


        if (columnFromTemplate) {
            return columnFromTemplate
        }

        else {
            return {
                header: column.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
                accessorKey: column,
                accessorFn: (row) => {
                    let value = row[column]
                    if (Number(value)) {
                        return parseFloat(Number(value || 0).toFixed(2))
                    }
                    return value || 0
                }
            }
        }


    })

    console.log('generated columns', generatedColumns)


    return generatedColumns
}

export function addTotalColumnForPayrollEntries(payrollEntries = []) {
    if (!payrollEntries.length) return [];

    return payrollEntries.map((entry) => {
        let amountTotal = total(entry)
        return {
            ...entry,
            total: amountTotal 
        }
    })
}

export function addTotalDeductionsForPayrollEntries(payrollEntries = []) {
    if (!payrollEntries.length) return [];

    //total + sss_er

    return payrollEntries.map((entry) => {
        let amountTotalDeductions = totalDeductions(entry)
        return {
            ...entry,
            total_deductions: amountTotalDeductions
        }
    })
}

export function addGrossBillingForPayrollEntries(payrollEntries = []) {
    if (!payrollEntries.length) return [];

    let updatedEntries = payrollEntries.map((entry) => {
        let amountGrossBilling = grossBilling(entry)
        // console.log('computing gross billing for entry', 'total', total, 'total deductions', totalDeductions, 'gross billing', gross_billing)

        return {
            ...entry,
            gross_billing: amountGrossBilling
        }
    })


    return updatedEntries


}


export function fetchAllColumnsFromPayrollEntries(payrollEntries = []) {
    if (!payrollEntries.length) return [];

    let allColumns = new Set();

    payrollEntries.forEach((entry) => {
        Object.keys(entry).forEach((key) => {
            allColumns.add(key);
        });
    });

    return Array.from(allColumns);
}


export default {
    generateColumnsForPayroll,
    fetchAllColumnsFromPayrollEntries,
    addTotalColumnForPayrollEntries,
    addTotalDeductionsForPayrollEntries,
    addGrossBillingForPayrollEntries
}

